/**
 * @fileoverview Logout route that clears tokens synchronously and hard-redirects to landing page.
 * @module features/auth/pages/LogoutPage
 */

import { ReactElement, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { clearOnboardingState } from '@/features/onboarding/hooks/useOnboardingState'
import { clearSupabaseAuthTokensSync, storeLastUserId, supabase } from '@/lib/supabaseClient'

/**
 * Logout page component. On mount, clears local credentials and redirects.
 *
 * @component
 */
export function LogoutPage(): ReactElement {
  const { t } = useTranslation('features-auth')

  useEffect(() => {
    // Store current user ID before clearing tokens (for cache invalidation on next login)
    storeLastUserId()
    try {
      // Clear tenant selection
      localStorage.removeItem('activeOrgId')
      // Clear legacy Basic Auth credentials if present
      localStorage.removeItem('authCredentials')
      sessionStorage.removeItem('authCredentials')
      // Clear onboarding state to prevent stale data on re-login
      clearOnboardingState()
    } catch {
      // ignore
    }
    // Clear Supabase tokens synchronously to avoid races
    clearSupabaseAuthTokensSync()
    // Fire-and-forget server-side sign out
    void supabase.auth.signOut()
    // Hard redirect to landing page to reset app state completely
    window.location.replace('/')
  }, [])

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6">
      <div className="text-sm text-muted-foreground">{t('logout.signingOut')}</div>
    </div>
  )
}
